import React from 'react'
import { InboxTab } from '../home/inboxes'
import { User } from '../../providers/context'
import { useRequest } from '../../request'
import { Loader } from '../../components/simple/loader'
import { Button } from '../../components/simple/button'
import { useSmartLinkTo } from '../../components/simple/link'
import { MessageBase } from '../home/message'
import { MiniPie, MiniPieData } from '../../components/charts/miniPie'

interface Assessment {
  id: number
  name: string
  date: string
  expiryDate: string
  school: string
  pie?: MiniPieData
}

export const Assessments: React.FC<{
  user: User
  inboxTab: { tab: InboxTab }
}> = ({ user, inboxTab }) => {
  const { data: assessments, loading } = useRequest<{
    active: Assessment[]
    inactive: Assessment[]
  }>(
    '/frontend_api/getAssessments',
    inboxTab // passed in as data to make it reload when the tab changes (tab changes indicates a reload is needed)
  )
  return (
    <div
      className={`flex-grow overflow-y-auto overflow-x-hidden bg-slate-50 px-[.375rem] ${assessments && loading ? 'pointer-events-none opacity-50' : ''}`}
    >
      {!assessments ? (
        <Loader />
      ) : (
        <>
          <div className="mb-2 mt-3 flex justify-center gap-8 text-center">
            <b className="text-lg">Document Delivery</b>
            {user.showAssessments && user.type == 'teacher' && (
              <Button logo small link="/assessment/create">
                New Document
              </Button>
            )}
          </div>
          {assessments.active.length === 0 && <div className="mt-4 text-center">No Current Documents</div>}
          {assessments.active.map((assessment) => (
            <AssessmentRow assessment={assessment} key={assessment.id} user={user} />
          ))}
          {assessments.inactive.length > 0 && (
            <>
              <div className="mb-2 mt-3 text-center text-lg font-bold">Document History</div>
              <div className="opacity-75">
                {assessments.inactive.map((assessment) => (
                  <AssessmentRow assessment={assessment} key={assessment.id} user={user} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

const AssessmentRow: React.FC<{ assessment: Assessment; user: User }> = ({ assessment, user }) => {
  const smartLinkTo = useSmartLinkTo()
  return (
    <MessageBase
      onClick={smartLinkTo(`/assessment/${user.type === 'teacher' ? 'view' : 'viewLatestInvite'}/${assessment.id}`)}
      avatar={{
        color: '#2295f0',
        img: 'icon-assessment',
      }}
      top={
        <>
          <div className="flex-1 font-bold">{assessment.name}</div>
          {user.type == 'teacher' && (
            <div className="ml-auto flex-shrink-0 font-semibold opacity-60">{assessment.date}</div>
          )}
        </>
      }
      bottom={
        <>
          <div className="text-sm font-bold opacity-50">{assessment.school}</div>
          {user.type == 'teacher' && assessment.expiryDate && (
            <div className="ml-auto text-sm font-bold opacity-50">until {assessment.expiryDate}</div>
          )}
          {assessment.pie && <MiniPie className="ml-auto" data={assessment.pie} />}
        </>
      }
    />
  )
}
